import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileDown, Receipt, AlertTriangle } from "lucide-react";
import { getReceipt, receiptPdfUrl, zar } from "@/api";

export default function ReceiptView() {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getReceipt(token)
      .then(setReceipt)
      .catch(() => setError("This receipt link is invalid. Check the link in your email or call 010 534 9926."));
  }, [token]);

  if (error) {
    return (
      <div data-testid="receipt-error" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <div>
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-white mb-3">Receipt not found</h1>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="receipt-loading">Loading receipt…</div>;
  }

  const rows = [
    ["Receipt number", receipt.receipt_no],
    ["Date", receipt.date],
    ["Client", receipt.full_name],
    ["Reference", receipt.reference],
    ["Description", receipt.description],
    ["Payment method", "Card (secure online checkout)"],
  ];

  return (
    <div data-testid="receipt-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3 mb-6">
            <Receipt size={26} className="text-[#F57C00]" />
            <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold">CompCar Motors</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-8">
            Payment received. Thank you.
          </h1>

          <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden mb-8" data-testid="receipt-card">
            <div className="px-8 py-6 border-b border-sky-300/10 flex justify-between items-center">
              <span className="text-sm font-bold text-sky-300 uppercase tracking-wider">Amount paid</span>
              <span className="font-display text-3xl font-black text-[#F57C00]" data-testid="receipt-amount">{zar(receipt.amount)}</span>
            </div>
            <div className="px-8 py-6">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between items-center text-sm border-b border-white/5 py-3 last:border-0">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-semibold text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>

          <a
            href={receiptPdfUrl(token)}
            data-testid="receipt-download-btn"
            className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-7 py-4 rounded-md transition-colors duration-300 mb-6"
          >
            <FileDown size={18} /> Download receipt PDF
          </a>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Every on-time payment builds your ownership balance. Questions about
            this receipt? Call 010 534 9926 or email admin@compcarmotors.co.za.{" "}
            <Link to="/portal" className="text-sky-300 hover:text-white">Open your client portal</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
